import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Delete, Search } from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { textFilterToRegExp, useLocales, useTimezones } from '../../../../hooks';
import Button from '../../../shared/Button';
import { DataGrid, DataGridColDef } from '../../../shared/DataGrid';
import TextField from '../../../shared/TextField';
import { PermissionsGroupId, CampaignResponse } from '../../../../API';
import { campaignManagerTestIds } from '../../../shared/TestsIds';
import IconButton from '../../../shared/IconButton';
import { useData } from '../../../../data-layer';
import { usePermissions } from '../../../../hooks/Permissions/usePermissions';
import { useRecoilState, useRecoilValue } from 'recoil';
import { HomepageOptions } from '../../../../state/theme';
import { usePermissionsGuard } from '../../../../hooks/General/usePermissionsGuard';
import { withCampaignsSplitPane } from '../../../../state/General';
import { AppRoutes } from '../../../../Routes';
import { withCampaignsFilters } from '../../../../state/Payments/withPaymentsFilters';
import { IdBadge } from '../../../shared/IdBadge';
import CountryBadge from '../../../shared/CountryBadge';

const useStyles = makeStyles()((theme) => ({
  root: { display: 'flex', flexDirection: 'column', flexGrow: 1, padding: theme.spacing(4) },
  filters: { display: 'flex', gap: theme.spacing(4), marginBottom: theme.spacing(4) },
  textFilterContainer: { flexGrow: 1, display: 'flex', gap: theme.spacing(4) },
  textFilter: { width: 500, marginBottom: '0 !important' },
  gridContainer: { display: 'flex', flexGrow: 1 }
}));

export function CampaignManager(): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { timestamp } = useTimezones();
  const navigate = useNavigate();
  const {
    campaigns: {
      state: { withAllRecords: withAllCampaigns },
      hook: { new: newCampaign, edit: editCampaign, remove: removeCampaign }
    }
  } = useData();
  const campaigns = useRecoilValue(withAllCampaigns);

  const { canSave, canDelete, canRead } = usePermissionsGuard({
    homepageOption: HomepageOptions.MONETIZATION
  });
  const { hasUpsertPermission, hasDeletePermission, hasReadPermission } = usePermissions();

  const hasMonetizationReadPermissions = hasReadPermission(PermissionsGroupId.MONETIZATION) || canRead;

  const [filters, setFilters] = useRecoilState(withCampaignsFilters);
  const [filteredCampaigns, setFilteredCampaigns] = useState<CampaignResponse[] | undefined>([]);

  useEffect(() => {
    if (!hasMonetizationReadPermissions) {
      navigate(AppRoutes.welcome);
    }
  }, [hasMonetizationReadPermissions]);

  useEffect(() => {
    updateFilteredCampaigns();
  }, [campaigns, filters]);

  const isLoaded = !!campaigns;

  const deleteCampaignHandler = (campaign: CampaignResponse) => {
    if (window.confirm(t('payments.campaign.delete_confirmation'))) {
      removeCampaign(campaign.id);
    }
  };

  const columns: DataGridColDef<CampaignResponse>[] = [
    {
      key: 'sponsorId',
      label: t('payments.campaign.sponsorId'),
      cellRenderer: (campaign) => <IdBadge key={`campaing_sponsor_${campaign.sponsorId}`} id={campaign.sponsorId} />,
      baseWidth: '20%'
    },
    {
      key: 'startDate',
      label: t('payments.campaign.startDate'),
      cellRenderer: (campaign) => timestamp(campaign.startDate),
      baseWidth: '15%'
    },
    {
      key: 'endDate',
      label: t('payments.campaign.endDate'),
      cellRenderer: (campaign) => timestamp(campaign.endDate),
      baseWidth: '15%'
    },
    {
      key: 'contentIds',
      label: t('payments.campaign.contentIds'),
      cellRenderer: (campaign) => (
        <>
          {campaign.contentIds.map((contentId) => (
            <IdBadge key={`campaing_content_${contentId}`} id={contentId} />
          ))}
        </>
      ),
      baseWidth: '20%'
    },
    {
      key: 'countries',
      label: t('payments.campaign.countries'),
      cellRenderer: (campaign) => (
        <>
          {campaign.countries.map((country) => (
            <CountryBadge key={`campaing_country_${country}`} countryCode={country.toUpperCase()} />
          ))}
        </>
      ),
      baseWidth: '15%'
    },
    {
      key: 'trackingUrl',
      label: t('payments.campaign.tracking_url'),
      cellRenderer: (campaign) => (campaign.trackingUrl ? <IdBadge id={campaign.trackingUrl} /> : '-'),
      baseWidth: '15%'
    },
    {
      key: 'actions',
      label: t('general.actions'),
      cellRenderer: (campaign) => (
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            deleteCampaignHandler(campaign);
          }}
          title={t('general.delete')}
          size="large"
          disabled={!hasDeletePermission(PermissionsGroupId.MONETIZATION) && !canDelete}
          data-testid={campaignManagerTestIds.deleteCampaignButton}
        >
          <Delete />
        </IconButton>
      ),
      baseWidth: '10%'
    }
  ];

  const onCampaignClick = (campaign: CampaignResponse) => {
    if (!isLoaded) return;
    editCampaign(campaign.id);
  };

  const onTextFilterChanged: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
    setFilters({
      ...filters,
      text: evt.target.value
    });
  };

  const updateFilteredCampaigns = () => {
    if (!campaigns) return;
    const newFilteredPromotions = campaigns.filter((campaign) => {
      if (filters.text) {
        const textFilterRgx = textFilterToRegExp(filters.text);
        return (
          textFilterRgx.test(campaign.sponsorId) || (campaign.trackingUrl && textFilterRgx.test(campaign.trackingUrl))
        );
      }
      return true;
    });
    setFilteredCampaigns(newFilteredPromotions);
  };

  return (
    <div className={classes.root} data-testid={campaignManagerTestIds.root}>
      <div className={classes.filters}>
        <div className={classes.textFilterContainer}>
          <TextField
            clearable
            label={t('payments.campaign.filter_by_text')}
            className={classes.textFilter}
            value={filters.text}
            onChange={onTextFilterChanged}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            data-testid={campaignManagerTestIds.textFilter}
          />
        </div>
        <Button
          endIcon={<Add />}
          disabled={!isLoaded || (!hasUpsertPermission(PermissionsGroupId.MONETIZATION) && !canSave)}
          onClick={newCampaign}
          data-testid={campaignManagerTestIds.newCampaignButton}
        >
          {t('payments.campaign.new_campaign')}
        </Button>
      </div>
      <div className={classes.gridContainer}>
        <DataGrid
          rows={filteredCampaigns}
          columns={columns}
          onRowClick={(campaign) => onCampaignClick(campaign)}
          withColumnSizes={withCampaignsSplitPane}
          useVirtualization
          rowKey="id"
        />
      </div>
    </div>
  );
}
